/**
 * Scrubs free text before it leaves the browser in a feedback report (the
 * recent client errors; the person's own message is theirs to write), and
 * the text of a page's picture (redactPageText). The server
 * (functions/src/services/suiteFeedback.ts redact) applies the same rules
 * again, and both share the same test cases. Removed:
 *   - a URL's user:password@, query string and fragment, absolute or
 *     relative (/callback#..., ./x?..., ../x#...), and any bare
 *     ?key=value / #key=value;
 *   - HTTP credentials (Basic / Bearer / Digest and what follows);
 *   - the value of any key=value, key: value or "key": "value" whose key
 *     names a secret (isSecretKey): it contains pass, pwd, otp, token,
 *     secret, auth, session, bearer, credential, cookie, signature or
 *     apikey, or one of its parts (split at . _ - and camelCase) is key,
 *     code, sig, pin or verifier (so key-id, code-verifier, 2fa_code; not
 *     keyboard). Keys are looked at one by one, so "Error: password=x"
 *     still loses x; a quoted value goes up to its closing quote, or to the
 *     end of the text when there is none;
 *   - JWTs, email addresses, and any run of 24+ token characters;
 *   - (errors only) numbers of 6 or more digits, with or without separators
 *     (an authenticator code, a phone number).
 * Every pattern is bounded (no part can run on unbounded), so scrubbing is
 * linear in the text's length; errors are also cut to MAX_SCRUB characters
 * before they are scrubbed.
 * Names and other free words cannot be told apart from ordinary text, which
 * is why errors are kept short and only real error objects are read
 * (errorBuffer.js).
 */
export const MAX_SCRUB = 2000;

const JWT_RE = /\beyJ[A-Za-z0-9_-]{5,2000}\.[A-Za-z0-9_-]{5,2000}\.[A-Za-z0-9_-]{5,2000}/g;
const SCHEME = "[a-z][a-z0-9+.-]{0,20}:\\/\\/";
// user:password@ after a scheme, or after // (a protocol-relative URL).
const USERINFO_RE = new RegExp(`(\\b${SCHEME}|(?:^|[\\s"'(=,])\\/\\/)[^\\s/@"'<>]{1,256}@`, "gi");
const URL_TAIL_RE = new RegExp(`(\\b${SCHEME}[^\\s?#"'<>)]{1,2048})[?#][^\\s"'<>)]*`, "gi");
const REL_TAIL_RE = /(^|[\s"'(=,])(\.{0,2}\/[^\s?#"'<>)]{0,512})[?#][^\s"'<>)]*/g;
const BARE_QUERY_RE = /[?#&][A-Za-z0-9_.-]{1,64}=[^\s"'<>)]*/g;
const AUTH_SCHEME_RE = /\b(Basic|Bearer|Digest)\s+[A-Za-z0-9._~+/=-]+/gi;
// A key (and a closing quote); the separator and the value are found by hand (scrubPairs).
const KEY_RE = /\b([A-Za-z0-9_][\w.-]{0,63})["']?/g;
const SPACE = /\s/;
const EMAIL_RE = /[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9.-]{1,253}\.[A-Za-z]{2,24}/g;
const LONG_TOKEN_RE = /[A-Za-z0-9_\-+]{24,}/g;
// No lookbehind (older Safari cannot parse it): the character before is captured and kept.
const NUMBER_RE = /(^|[^A-Za-z0-9_])(\+?\(?\d[\d\s().-]{4,40}\d)(?![A-Za-z0-9_])/g;

const SECRET_WORDS = /pass|pwd|otp|token|secret|auth|session|bearer|credential|cookie|signature|apikey/i;
const SECRET_PARTS = new Set(["key", "code", "sig", "pin", "verifier"]);

/** Whether a key=value key names a secret (see the file header). */
export function isSecretKey(key) {
  if (typeof key !== "string" || !key) return false;
  if (SECRET_WORDS.test(key)) return true;
  return key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").split(/[\s._-]+/).some((p) => SECRET_PARTS.has(p.toLowerCase()));
}

const VALUE_END = new Set([" ", "\t", "\n", "\r", ",", ";", "&", "\"", "'", "}", ")", "]"]);

/** Where a value that starts at i ends (exclusive). */
function valueEnd(text, i) {
  const q = text[i];
  if (q === "\"" || q === "'") {
    for (let j = i + 1; j < text.length; j++) {
      if (text[j] === "\\") { j++; continue; }
      if (text[j] === q) return j + 1;
    }
    return text.length; // no closing quote: up to the end
  }
  let j = i;
  while (j < text.length && !VALUE_END.has(text[j])) j++;
  return j;
}

/** Redacts the value of every key that names a secret, looking at each key. */
export function scrubPairs(text, mask) {
  let out = "";
  let from = 0;
  KEY_RE.lastIndex = 0;
  for (let m = KEY_RE.exec(text); m; m = KEY_RE.exec(text)) {
    // Any spaces or line breaks, then = or :, then any spaces: one pass forward.
    let j = m.index + m[0].length;
    while (j < text.length && SPACE.test(text[j])) j++;
    if (text[j] !== "=" && text[j] !== ":") { KEY_RE.lastIndex = m.index + m[0].length; continue; }
    j++;
    while (j < text.length && SPACE.test(text[j])) j++;
    const valueStart = j;
    if (!isSecretKey(m[1])) {
      // Not a secret key: look again just after its separator, so a pair
      // inside its value ("Error: password=x") is still found.
      KEY_RE.lastIndex = valueStart;
      continue;
    }
    const end = valueEnd(text, valueStart);
    if (end === valueStart) { KEY_RE.lastIndex = valueStart; continue; }
    out += text.slice(from, valueStart) + mask;
    from = end;
    KEY_RE.lastIndex = end;
  }
  return out + text.slice(from);
}

function scrub(text, m) {
  const step = text
    .replace(JWT_RE, m.token)
    .replace(USERINFO_RE, `$1${m.secret}@`)
    .replace(URL_TAIL_RE, `$1${m.tail}`)
    .replace(REL_TAIL_RE, `$1$2${m.tail}`)
    .replace(BARE_QUERY_RE, m.tail)
    .replace(AUTH_SCHEME_RE, `$1 ${m.secret}`);
  return scrubPairs(step, m.secret)
    .replace(EMAIL_RE, m.email)
    .replace(LONG_TOKEN_RE, m.secret);
}

const ERROR_MASKS = { token: "[token]", secret: "[redacted]", tail: "?[…]", email: "[email]" };
const PAGE_MASKS = { token: "••••", secret: "••••", tail: "••••", email: "••••@••••" };

/** For error text: cut to MAX_SCRUB first (the caller keeps far less). */
export function redact(text) {
  if (typeof text !== "string") return "";
  return scrub(text.slice(0, MAX_SCRUB), ERROR_MASKS)
    .replace(NUMBER_RE, (_m, pre, num) => `${pre}${(num.match(/\d/g) || []).length >= 6 ? "[number]" : num}`);
}

/**
 * The picture's text scrub (screenshot.js): the same rules except the
 * number rule, since amounts and dates on a page matter in a bug picture
 * (an authenticator code is only ever typed, and text boxes are drawn
 * empty).
 */
export function redactPageText(text) {
  if (typeof text !== "string") return text;
  return scrub(text, PAGE_MASKS);
}

export function clip(text, max) {
  if (typeof text !== "string") return "";
  const t = text.trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}
